import {
  DEFAULT_PLAN_DEFINITIONS,
  mergePlanDefinitions,
  type PlanDefinition,
  type PlanId,
} from '@shopgnd/shared';
export type PlanSettingsCommissionSource = {
  planDefinitionsJson?: string;
  starterCommissionBps: number;
  growthCommissionBps: number;
  proCommissionBps: number;
};

let cachedPlans: PlanDefinition[] = DEFAULT_PLAN_DEFINITIONS;

function commissionBpsForPlan(settings: PlanSettingsCommissionSource, id: PlanId, fallback: number): number {
  if (id === 'starter') return settings.starterCommissionBps;
  if (id === 'growth') return settings.growthCommissionBps;
  if (id === 'pro') return settings.proCommissionBps;
  return fallback;
}

/** Keep marketing bullet commission % in sync with stored bps. */
function syncCommissionFeatureBullets(plan: PlanDefinition): PlanDefinition {
  const pct =
    plan.commissionBps % 100 === 0
      ? String(plan.commissionBps / 100)
      : (plan.commissionBps / 100).toFixed(1);
  return {
    ...plan,
    marketingFeatures: (plan.marketingFeatures ?? []).map((line) => {
      if (!/commission/i.test(line)) return line;
      return line.replace(/\d+(\.\d+)?%\s*platform commission/i, `${pct}% platform commission`);
    }),
  };
}

/** Plans for /sell and hub — use planDefinitionsJson when present; else fee-tab scalar fields. */
export function resolvePlanDefinitionsFromSettings(
  settings: PlanSettingsCommissionSource,
): PlanDefinition[] {
  const hasStoredPlans = Boolean(settings.planDefinitionsJson?.trim());
  const plans = parsePlanDefinitionsJson(settings.planDefinitionsJson);
  return plans.map((plan) => {
    const commissionBps = hasStoredPlans
      ? plan.commissionBps
      : commissionBpsForPlan(settings, plan.id, plan.commissionBps);
    return syncCommissionFeatureBullets({ ...plan, commissionBps });
  });
}

export function applyPlanDefinitions(definitions: PlanDefinition[]): void {
  cachedPlans = definitions;
}

export function getRuntimePlanDefinitions(): PlanDefinition[] {
  return cachedPlans;
}

export function parsePlanDefinitionsJson(raw: string | undefined | null): PlanDefinition[] {
  if (!raw?.trim()) return DEFAULT_PLAN_DEFINITIONS;
  try {
    const parsed = JSON.parse(raw) as PlanDefinition[];
    if (!Array.isArray(parsed)) return DEFAULT_PLAN_DEFINITIONS;
    return mergePlanDefinitions(parsed);
  } catch {
    return DEFAULT_PLAN_DEFINITIONS;
  }
}

export function serializePlanDefinitions(definitions: PlanDefinition[]): string {
  return JSON.stringify(definitions, null, 2);
}
